import React from 'react';
import { FileText, Headphones, Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const commMediaSrc = (url, download = false) => {
  const base = url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return download ? `${base}?download=true` : base;
};

const fmtSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// CTH-COM-087 — audio player + PDF access for communication attachments.
export const CommAttachments = ({ attachments = [], isES = true }) => {
  if (!attachments.length) return null;
  const L = (es, en) => (isES ? es : en);
  const audios = attachments.filter((a) => a.kind === 'audio');
  const pdfs = attachments.filter((a) => a.kind === 'pdf');
  return (
    <div className="mt-3 space-y-2" data-testid="comm-attachments">
      {audios.map((a) => (
        <div key={a.file_id} className="rounded-lg border bg-slate-50/70 p-3" data-testid={`comm-audio-${a.file_id}`}>
          <div className="flex items-center gap-2 mb-2">
            <Headphones className="h-4 w-4 text-teal-700 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate">{a.name}</p>
              {a.description && <p className="text-[11px] text-muted-foreground truncate">{a.description}</p>}
            </div>
            {a.size ? <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{fmtSize(a.size)}</span> : null}
          </div>
          <audio controls preload="metadata" className="w-full h-9" src={commMediaSrc(a.url)}
                 data-testid={`comm-audio-player-${a.file_id}`}>
            {L('Su navegador no soporta audio embebido.', 'Your browser does not support embedded audio.')}
          </audio>
        </div>
      ))}
      {pdfs.map((a) => (
        <div key={a.file_id} className="rounded-lg border bg-slate-50/70 p-3 flex items-center gap-3 flex-wrap"
             data-testid={`comm-pdf-${a.file_id}`}>
          <FileText className="h-4 w-4 text-rose-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold truncate">{a.name}</p>
            {a.description && <p className="text-[11px] text-muted-foreground truncate">{a.description}</p>}
            {a.size ? <p className="text-[10px] text-muted-foreground">{fmtSize(a.size)}</p> : null}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => window.open(commMediaSrc(a.url), '_blank', 'noopener')}
                    data-testid={`comm-pdf-open-${a.file_id}`}>
              <ExternalLink className="h-3 w-3 mr-1" />{L('Abrir', 'Open')}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => { const link = document.createElement('a'); link.href = commMediaSrc(a.url, true); link.download = a.name || 'document.pdf'; document.body.appendChild(link); link.click(); link.remove(); }}
                    data-testid={`comm-pdf-download-${a.file_id}`}>
              <Download className="h-3 w-3 mr-1" />{L('Descargar', 'Download')}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
